/* eslint-disable @typescript-eslint/only-throw-error */
import { type LoaderFunction, redirect } from 'react-router'
import type { CreateCallCenterRoutesOptions } from '@/features/call-center/routes'
import type { CallCenterViewModel } from '@/features/call-center/CallCenterViewModel'
import type { CallCenterJobPageViewModel } from '@/features/call-center/CallCenterJobPageViewModel'
import { Id } from '@/infra/id'
import { links } from '@/utils/link'

/**
 * Data returned by {@link createCallCenterViewLoader}. `null` when no
 * workspace is selected yet, so the authentication screen is showing.
 */
export type CallCenterViewLoaderData = { vm: CallCenterViewModel } | null

/**
 * Data returned by {@link createCallCenterJobPageLoader}. `null` when no
 * workspace is selected yet, so the authentication screen is showing.
 */
export type CallCenterJobPageLoaderData = {
  vm: CallCenterJobPageViewModel
} | null

/**
 * Creates a loader function for the call center that provides its view model,
 * and redirects users who may not use the call center.
 *
 * @param options
 */
export function createCallCenterViewLoader(
  options: CreateCallCenterRoutesOptions,
): LoaderFunction {
  return async ({ request: { signal } }): Promise<CallCenterViewLoaderData> => {
    const { workspace } = await options.waitForAuthResolution(signal)
    if (!workspace) {
      return null
    }

    const internal = await options.callCenter.internal()
    const vm = await internal.acquireCallCenterViewModel(signal)
    if (!vm.canAccessCallCenter) {
      throw redirect(links.welcome())
    }

    return { vm }
  }
}

/**
 * Creates a loader function for the call center's job page that provides the
 * view model for the route's job.
 *
 * @param options
 */
export function createCallCenterJobPageLoader(
  options: CreateCallCenterRoutesOptions,
): LoaderFunction {
  return async ({
    params,
    request: { signal },
  }): Promise<CallCenterJobPageLoaderData> => {
    const { workspace } = await options.waitForAuthResolution(signal)
    if (!workspace) {
      return null
    }

    const internal = await options.callCenter.internal()
    const vm = await internal.acquireCallCenterJobPageViewModel(
      Id.assert(params.jobId),
      signal,
    )
    return { vm }
  }
}
